import { TABLE_TYPE } from 'components/PayrollTable/constants';
import intl from 'react-intl-universal';
import { Button, Tooltip } from '@mui/material';
import { formatTime } from 'utils/formatTime';
import StarIcon from '@mui/icons-material/Star';
import PropTypes from 'prop-types';
import logger from 'utils/logger';
import { formatDateForLanguage } from 'views/Billing/shared/utils';

const log = logger.createLogger('CommonColumns');

export const BillingMonthColumn = () => {
  return {
    accessorKey: 'billingMonth',
    header: intl.get('billing_for_sent_cases_subtitle_billing_month'),
    enableSorting: true,
    enableColumnFilter: false,
    Cell: ({ renderedCellValue }) => {
      return billingMonthCell({
        startDate: renderedCellValue.startDate,
        endDate: renderedCellValue.endDate,
      });
    },
  };
};

const billingMonthCell = ({ startDate, endDate }) => {
  return (
    <p>
      {formatDateForLanguage(startDate)} - {formatDateForLanguage(endDate)}
    </p>
  );
};

billingMonthCell.propTypes = {
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
};

export const FromOrganizationNameColumn = () => {
  return {
    accessorKey: 'fromOrganizationName',
    header: intl.get('billing_for_received_cases_label_column_received_from'),
    enableSorting: true,
    enableColumnFilter: true,
  };
};

export const ToOrganizationNameColumn = () => {
  return {
    accessorKey: 'toOrganizationName',
    header: intl.get('billing_for_sent_cases_label_column_sent_to'),
    enableSorting: true,
    enableColumnFilter: true,
  };
};

export const TotalPriceColumn = () => {
  return {
    accessorKey: 'totalPrice',
    header: intl.get('billing_for_sent_cases_label_column_total_price'),
    enableSorting: true,
    enableColumnFilter: true,
    // TODO: format price
    Cell: totalPriceCell,
  };
};

const totalPriceCell = ({ renderedCellValue }) => {
  const totalFee = renderedCellValue;
  return <p>￥{totalFee}</p>;
};

totalPriceCell.propTypes = {
  renderedCellValue: PropTypes.number.isRequired,
};

export const DetailsColumn = (
  setSelectedBillingMonth,
  setSelectedDstOrgName,
  setSelectedDstOrgId,
  setTableType,
  doesHaveKenshinPermission
) => {
  return {
    accessorKey: 'details',
    header: intl.get('billing_for_sent_cases_label_column_details'),
    enableSorting: false,
    enableColumnFilter: false,
    enableColumnActions: false,
    Cell: ({ renderedCellValue }) =>
      detailsCell({
        renderedCellValue,
        setSelectedBillingMonth,
        setSelectedDstOrgName,
        setSelectedDstOrgId,
        setTableType,
        doesHaveKenshinPermission,
      }),
  };
};

const detailsCell = ({
  renderedCellValue,
  setSelectedBillingMonth,
  setSelectedDstOrgName,
  setSelectedDstOrgId,
  setTableType,
  doesHaveKenshinPermission,
}) => {
  const { billingMonth, organizationName, organizationId } = renderedCellValue;
  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          setSelectedBillingMonth(billingMonth);
          setSelectedDstOrgName(organizationName);
          setSelectedDstOrgId(organizationId);
          setTableType(TABLE_TYPE.DETAILED);
        }}>
        {intl.get('billing_for_sent_cases_button_go_to_details_seimitsu')}
      </Button>
      {doesHaveKenshinPermission && (
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setSelectedBillingMonth(billingMonth);
            setSelectedDstOrgName(organizationName);
            setSelectedDstOrgId(organizationId);
            setTableType(TABLE_TYPE.DETAILED_KENSHIN);
          }}>
          {intl.get('billing_for_sent_cases_button_go_to_details_kenshin')}
        </Button>
      )}
    </div>
  );
};

detailsCell.propTypes = {
  renderedCellValue: PropTypes.object.isRequired,
  setSelectedBillingMonth: PropTypes.func.isRequired,
  setSelectedDstOrgName: PropTypes.func.isRequired,
  setSelectedDstOrgId: PropTypes.func.isRequired,
  setTableType: PropTypes.func.isRequired,
  doesHaveKenshinPermission: PropTypes.bool.isRequired,
};

export const IssueTitleColumn = () => {
  return {
    accessorKey: 'issueTitle',
    header: intl.get('billing_for_sent_cases_label_column_case_title'),
    enableSorting: true,
    enableColumnFilter: true,
  };
};

export const EmergencyColumn = () => {
  return {
    accessorKey: 'emergency',
    header: intl.get('billing_for_sent_cases_label_column_emergency'),
    enableSorting: true,
    enableColumnFilter: true,
  };
};

export const OrderTimeColumn = () => {
  return {
    accessorKey: 'orderTime',
    header: intl.get('billing_for_sent_cases_label_column_order_time'),
    enableSorting: true,
    enableColumnFilter: true,
    Cell: ({ renderedCellValue }) => {
      return formatTime(renderedCellValue);
    },
  };
};

export const CompleteTimeColumn = () => {
  return {
    accessorKey: 'completeTime',
    header: intl.get('billing_for_sent_cases_label_column_complete_time'),
    enableSorting: true,
    enableColumnFilter: true,
    Cell: ({ renderedCellValue }) => {
      return formatTime(renderedCellValue);
    },
  };
};

export const PriceColumn = () => {
  return {
    accessorKey: 'price',
    header: intl.get('billing_for_sent_cases_label_column_price'),
    enableSorting: true,
    enableColumnFilter: true,
    Cell: priceCellWithCurrentFee,
  };
};

const priceCellWithCurrentFee = ({ renderedCellValue }) => {
  const currentFee =
    renderedCellValue.currentFee ?? renderedCellValue.originalFee;
  return <p>￥{currentFee}</p>;
};

// TODO Might need to be removed or decide after rework
const priceCell = ({ renderedCellValue }) => {
  const originalFee = renderedCellValue.originalFee;
  const currentFee = renderedCellValue.currentFee;
  if (currentFee === null || currentFee === originalFee) {
    return <p>￥{originalFee}</p>;
  }
  return (
    <p>
      ￥{currentFee} <del>￥{originalFee}</del>
    </p>
  );
};

priceCell.propTypes = {
  renderedCellValue: PropTypes.object.isRequired,
};

export const ViewColumn = (openIssue) => {
  return {
    accessorKey: 'view',
    header: intl.get('billing_for_sent_cases_label_column_details'),
    enableSorting: false,
    enableColumnFilter: false,
    enableColumnActions: false,
    muiTableBodyCellProps: {
      onClick: (e) => e.stopPropagation(),
    },
    Cell: ({ renderedCellValue }) => viewCell({ renderedCellValue, openIssue }),
  };
};

const viewCell = ({ renderedCellValue, openIssue }) => {
  const issueId = parseInt(renderedCellValue.issueId);
  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        onClick={() => openIssue(issueId)}>
        {intl.get('billing_for_sent_cases_button_go_to_viewer')}
      </Button>
    </div>
  );
};

viewCell.propTypes = {
  renderedCellValue: PropTypes.object.isRequired,
  openIssue: PropTypes.func.isRequired,
};

export const FlagColumn = () => {
  return {
    accessorKey: 'flag',
    header: intl.get('billing_for_sent_cases_label_column_flag'),
    enableSorting: false,
    enableColumnFilter: false,
    enableColumnActions: false,
    Cell: ({ renderedCellValue }) => flagCell({ renderedCellValue }),
  };
};

const flagCell = ({ renderedCellValue }) => {
  const ifStarred = renderedCellValue;
  return (
    <div>
      {ifStarred && (
        <Tooltip
          title={intl.get('billing_for_sent_cases_detailed_tooltip_star_mark')}>
          <StarIcon fontSize="large" color="warning" />
        </Tooltip>
      )}
    </div>
  );
};

flagCell.propTypes = {
  renderedCellValue: PropTypes.bool.isRequired,
};
